import React from 'react';
import { Box, Center, Text, Link, Image, Divider } from '@chakra-ui/react';
import { useImaginationRoom } from '@invitato/imagination-room';

import useLang from '@/hooks/useLang';

import LazyImage from '@/components/Common/LazyImage';
import WithAnimation from '@/components/Common/WithAnimation';

import { ENABLE_COLLABORATION, SELECTED_VENDOR_COLLABORATION } from '@/constants/feature-flags';
import {
  ASSETS_BG_PATTERN,
  ASSETS_GEBYOK,
  ASSETS_FLOWER_1,
  ASSETS_FLOWER_2,
  ASSETS_WAYANG_SHINTA,
  ASSETS_WAYANG_RAMA,
  ASSETS_PURPLE_TREE,
  ASSETS_GUNUNGAN,
  IMG_LOGO,
  LOGO_INVITATO,
} from '@/constants/assets';
import { INVITATO_URL, SOUND_BY, SOUND_URL, THE_BRIDE } from '@/constants';
import { ENABLE_WAYANG } from '@/constants/feature-flags';
import { TEXT_PROPS } from '@/constants/default-props';

import { FOOTER_LINK_PROPS } from './types';
import txt from './locales';

function FooterSection() {
  const lang = useLang();
  const { onGetFinalName } = useImaginationRoom();

  return (
    <>
      <Box
        background="linear-gradient(to top, #9e907e 20%, transparent 100%)"
        height="100px"
        marginBottom="-8px"
      />
      <Box
        height="110vh"
        width="100%"
        bgImage={ASSETS_BG_PATTERN}
        bgSize="cover"
        bgPos="top center"
        position="relative"
      >
        {/* CLOSING */}
        <Center
          flexDir="column"
          bgColor="rgb(107 91 73 / 58%)"
          minHeight="100vh"
          width="100%"
          position="relative"
          overflow="hidden"
        >
          <Box width="97%" mx="auto" pos="relative" zIndex="1">
            <WithAnimation delay={1250}>
              <Image src={ASSETS_GEBYOK} mt="0px" />
            </WithAnimation>
          </Box>

          {/* LOGO */}
          <Box
            position="absolute"
            left="50%"
            transform="translateX(-50%)"
            top={{ '3sm': '45%', '2sm': '42%', sm: '40%' }}
            zIndex="3"
          >
            <WithAnimation top delay={1500}>
              <Text
                {...TEXT_PROPS.body}
                fontSize="20px"
                color="alternativeColorText"
                textAlign="center"
              >
                {txt.thanks[lang]}
              </Text>
              {IMG_LOGO ? (
                <LazyImage src={IMG_LOGO} maxW="110px" marginTop="14px" mx="auto" />
              ) : (
                <Text
                  margin="16px 0 32px 0"
                  {...TEXT_PROPS.subHeading}
                  color="alternativeColorText"
                  textAlign="center"
                  fontWeight="bold"
                  fontSize="42px"
                  dangerouslySetInnerHTML={{
                    __html: onGetFinalName('coupleName', THE_BRIDE)
                  }}
                />
              )}
            </WithAnimation>
          </Box>

          {/* GUNUNGAN  */}
          <Box
            className="animation__flower"
            position="absolute"
            top={{ '3sm': '4px', '2sm': '70px', '1sm': '4px', sm: '50px' }}
            left={{ '3sm': '-70px', '2sm': '-80px', sm: '-70px' }}
            zIndex="2"
          >
            <WithAnimation left delay={1500}>
              <Image
                src={ASSETS_GUNUNGAN}
                width={{ '3sm': '300px', '2sm': '290px', sm: '250px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            className="animation__flower-invert"
            position="absolute"
            top={{ '3sm': '4px', '2sm': '70px', '1sm': '4px', sm: '50px' }}
            right={{ '3sm': '-70px', '2sm': '-80px', sm: '-70px' }}
            zIndex="2"
          >
            <WithAnimation right delay={1500}>
              <Image
                src={ASSETS_GUNUNGAN}
                width={{ '3sm': '300px', '2sm': '290px', sm: '250px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>

          {/* TREES  */}
          <Box
            position="absolute"
            bottom={{ '3sm': '95px', '2sm': '175px', '1sm': '160px', sm: '80px' }}
            left={{ '3sm': '-160px', '2sm': '-160px', sm: '-165px' }}
            zIndex="2"
          >
            <WithAnimation left delay={1250}>
              <Image
                src={ASSETS_PURPLE_TREE}
                width={{ '3sm': '360px', '2sm': '320px', sm: '320px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>
          <Box
            position="absolute"
            bottom={{ '3sm': '95px', '2sm': '175px', '1sm': '160px', sm: '80px' }}
            right={{ '3sm': '-160px', '2sm': '-160px', sm: '-165px' }}
            zIndex="2"
          >
            <WithAnimation right delay={1250}>
              <Image
                src={ASSETS_PURPLE_TREE}
                width={{ '3sm': '360px', '2sm': '320px', sm: '320px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>

          {/* WAYANG  */}
          {ENABLE_WAYANG && (
            <>
              <Box
                className="animation-left-and-right"
                maxWidth="250px"
                position="absolute"
                bottom="95px"
                left={{ '3sm': '-50px', '2sm': '-80px', sm: '-65px' }}
                zIndex="3"
              >
                <WithAnimation left delay={1000}>
                  <Image
                    src={ASSETS_WAYANG_SHINTA}
                    width={{ '3sm': '180px', '2sm': '200px', sm: '170px' }}
                    transform="rotate(0deg)"
                  />
                </WithAnimation>
              </Box>
              <Box
                className="animation-right-and-left"
                maxWidth="250px"
                position="absolute"
                bottom="75px"
                right={{ '3sm': '-50px', '2sm': '-80px', sm: '-80px' }}
                zIndex="3"
              >
                <WithAnimation right delay={1000}>
                  <Image
                    src={ASSETS_WAYANG_RAMA}
                    width={{ '3sm': '180px', '2sm': '200px', sm: '170px' }}
                    transform="rotate(0deg)"
                  />
                </WithAnimation>
              </Box>
            </>
          )}

          {/* FLOWERS  */}
          <Box
            className="animation__flower"
            position="absolute"
            bottom="-60px"
            left="-50px"
            zIndex="3"
          >
            <WithAnimation left>
              <Image
                src={ASSETS_FLOWER_1}
                width={{ '3sm': '160px', '2sm': '180px', sm: '150px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>

          <Box
            className="animation__flower"
            position="absolute"
            bottom="-30px"
            left="40px"
            zIndex="2"
          >
            <WithAnimation bottom>
              <Image
                src={ASSETS_FLOWER_2}
                width={{ '3sm': '220px', '2sm': '260px', sm: '200px' }}
                transform="rotate(0deg)"
              />
            </WithAnimation>
          </Box>

          <Box
            className="animation__flower-invert"
            position="absolute"
            bottom="-50px"
            left="210px"
            zIndex="3"
            overflow="hidden"
          >
            <WithAnimation bottom>
              <Image
                src={ASSETS_FLOWER_2}
                width={{ '3sm': '220px', '2sm': '230px', sm: '200px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>

          <Box
            className="animation__flower-invert"
            position="absolute"
            bottom="-80px"
            right="-80px"
            zIndex="3"
            overflow="hidden"
          >
            <WithAnimation bottom>
              <Image
                src={ASSETS_FLOWER_1}
                width={{ '3sm': '210px', '2sm': '230px', sm: '190px' }}
                transform="rotateY(180deg)"
              />
            </WithAnimation>
          </Box>
        </Center>

        {/* INVITATO SECTION */}
        <Box
          {...TEXT_PROPS.body}
          pos="relative"
          minHeight="100px"
          width="100%"
          zIndex="3"
          bgColor="bgSecondary"
          padding="24px"
          textAlign="center"
          color="white"
        >
          {/* INVITATO SECTION */}
          <Center flexDir="column">
            <WithAnimation>
              <Center>
                <Link href={INVITATO_URL} target="_blank">
                  <LazyImage src={LOGO_INVITATO} maxWidth="110px" margin="0" />
                </Link>
              </Center>
            </WithAnimation>
            <WithAnimation>
              <Text textAlign="center" fontSize="sm" margin="0 0 12px 0">
                Created with love by Invitato
              </Text>
            </WithAnimation>
            {ENABLE_COLLABORATION && (
              <Box margin="-5px 0 16px">
                <Center>
                  <Divider
                    margin="5px 0 12px"
                    maxWidth="60px"
                    opacity="1"
                    height="1px"
                    bgColor="white"
                    border="none"
                    borderBottom="none"
                  />
                </Center>
                <Center>
                  <Link
                    href={`https://www.instagram.com/${SELECTED_VENDOR_COLLABORATION.instagram}/`}
                    target="_blank"
                  >
                    <Image
                      src={SELECTED_VENDOR_COLLABORATION.imgUrl}
                      {...SELECTED_VENDOR_COLLABORATION.imgProps}
                    />
                  </Link>
                </Center>
                <Center>
                  <Text color="white" align="center" fontSize="xs">
                    In Collaboration with {SELECTED_VENDOR_COLLABORATION.vendorName}
                  </Text>
                </Center>
              </Box>
            )}
            <Link {...FOOTER_LINK_PROPS} href={SOUND_URL} target="_blank">
              <Text
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
              >{`Song by ${SOUND_BY}`}</Text>
            </Link>
            <Text {...FOOTER_LINK_PROPS}>
              {`© ${new Date().getFullYear()} ${THE_BRIDE}. All Right Reserved`}
            </Text>
          </Center>
        </Box>
      </Box>
    </>
  );
}

export default React.memo(FooterSection);
